import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, User, ImpersonatableUser } from '../lib/api';
import { Cache } from '../lib/cache';
import { TokenManager } from '../lib/token-manager';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  // Impersonation
  isImpersonating: boolean;
  originalUser: User | null;
  startImpersonation: (
    targetUser: User | ImpersonatableUser,
    targetToken: string,
    originalToken: string,
    targetContextCompany?: { id: number; name: string },
    auditLogId?: number
  ) => void;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Accesses the current authentication state including user, token, and impersonation.
 * Must be used within an {@link AuthProvider}.
 *
 * @returns Auth state with login/logout actions and impersonation controls
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provides authentication state to the component tree.
 * Manages login/logout, token persistence via {@link TokenManager}, and user impersonation.
 * On mount, validates the stored token against the backend.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize state from secure storage
  const [user, setUser] = useState<User | null>(() => {
    const cached = TokenManager.getUser<User>();
    if (cached?.platform && !cached.company_type) {
      cached.company_type = cached.platform.toLowerCase() as User['company_type'];
    }
    // Set cache scope to persisted user on page refresh
    if (cached?.id) {
      Cache.setUserScope(cached.id);
    }
    return cached;
  });
  const [token, setToken] = useState<string | null>(() => {
    return TokenManager.getToken();
  });
  const [isLoading, setIsLoading] = useState(true);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState<boolean>(
    TokenManager.isImpersonating()
  );
  const [originalUser, setOriginalUser] = useState<User | null>(() => {
    return TokenManager.getOriginalUser<User>();
  });
  const [originalToken, setOriginalToken] = useState<string | null>(() => {
    return TokenManager.getOriginalToken();
  });

  const isAuthenticated = !!token && !!user;

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      // Set storage preference before storing the token
      TokenManager.setRememberMe(rememberMe);
      const response = await authAPI.login(email, password);
      // API returns standardized format: { status, message, data: { user, token } }
      const { user: userData, token: authToken } = response.data.data;

      // Derive company_type from platform if not present
      if (userData.platform && !userData.company_type) {
        userData.company_type = userData.platform.toLowerCase();
      }

      // Clear any existing impersonation state from previous sessions
      setIsImpersonating(false);
      setOriginalUser(null);
      setOriginalToken(null);
      TokenManager.stopImpersonation();

      // Clear stale page-level cache from previous session/user before storing new auth
      Cache.clearAll();

      // Scope cache to the new user so cached data is user-isolated
      Cache.setUserScope(userData.id);

      // Store auth data securely
      setUser(userData);
      setToken(authToken);
      TokenManager.setToken(authToken);
      TokenManager.setUser(userData);
    } catch (error: unknown) {
      console.error('Login error:', error);
      if (error instanceof Error && 'response' in error) {
        console.error('Error response:', (error as { response?: { data?: unknown } }).response?.data);
      }
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);

    // Clear impersonation state
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);

    // Clear all secure storage
    TokenManager.clearAll();

    // Clear page-level cache and reset user scope
    Cache.clearAll();
    Cache.setUserScope(null);

    // Call logout endpoint (fire and forget)
    authAPI.logout().catch(() => {
      // Ignore errors on logout
    });
  };

  const startImpersonation = (
    targetUser: User | ImpersonatableUser,
    targetToken: string,
    originalTok: string,
    targetContextCompany?: { id: number; name: string },
    auditLogId?: number
  ) => {
    // Derive company_type from platform if not present (same as login flow)
    const userWithType = { ...targetUser } as User;
    if (userWithType.platform && !userWithType.company_type) {
      userWithType.company_type = userWithType.platform.toLowerCase() as User['company_type'];
    }

    // Clear all caches before switching users
    Cache.clearAll();
    Cache.setUserScope(userWithType.id);
    // ViewContext persists portalMode/selectedCompany in its own localStorage
    // key; if we don't clear it the impersonated user inherits the admin's
    // sidebar + nav until they manually switch portals.
    localStorage.removeItem('helix_view_context_cache');

    // Persist the impersonation state BEFORE reload so it survives
    TokenManager.setToken(targetToken);
    TokenManager.setUser(userWithType);
    if (user) {
      TokenManager.startImpersonation(user, originalTok, auditLogId);
    }
    // CR-4858: Pin the access-context company (if the caller knew one) so the
    // post-reload ViewContext lands on the company the admin was inspecting,
    // not the impersonated user's primary company.
    if (targetContextCompany) {
      TokenManager.setImpersonationContext(targetContextCompany);
    }

    // Route the impersonation session to the correct landing page. The
    // previous hard-coded /purchasing broke for compliance-only tenants
    // (MAB) where the customer side never has a Purchasing menu. We now
    // pick a destination based on the target user's role + approval state,
    // and the Login.tsx effect will honour the tenant's
    // customer_default_landing_path on first paint after the reload.
    const role = (userWithType.company_type || userWithType.platform?.toLowerCase() || 'supplier').toLowerCase();
    let dest = '/dashboard';
    if (role === 'logistics') {
      dest = '/logistics/call-offs';
    } else if (role === 'customer') {
      // Pre-approval customers go straight to the onboarding progress page;
      // approved customers follow the tenant setting, which is applied by
      // the Login page redirect after settings finish loading.
      dest = userWithType.customer_is_approved === false
        ? '/onboarding/my-progress'
        : '/login';
    }
    window.location.href = dest;
  };

  const stopImpersonation = async () => {
    if (!originalToken) {
      console.error('No original token found');
      return;
    }

    try {
      const auditLogId = TokenManager.getAuditLogId() ?? undefined;
      const response = await authAPI.stopImpersonate(originalToken, auditLogId);
      // API returns standardized format: { status, message, data: { user, token } }
      const { user: restoredUser, token: restoredToken } = response.data.data;

      // Clear all caches
      Cache.clearAll();
      Cache.setUserScope(restoredUser.id);
      localStorage.removeItem('helix_view_context_cache');

      // Persist restored user BEFORE reload
      TokenManager.setToken(restoredToken);
      TokenManager.setUser(restoredUser);
      TokenManager.stopImpersonation();

      // Force a full page reload to clear all stale impersonation state
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error;
    }
  };

  const checkAuthStatus = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    // During impersonation, trust the persisted user/token from TokenManager.
    // The /auth/status/ endpoint uses SessionAuthentication (which has Mark's
    // session cookie) before TokenAuthentication, so it returns the original
    // user instead of the impersonated user. Skip the API call to avoid this.
    if (isImpersonating && user) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.status();
      // API returns standardized format: { status, data: { authenticated, user } }
      const statusData = response.data.data;
      if (statusData.authenticated && statusData.user) {
        // Derive company_type from platform if not present
        if (statusData.user.platform && !statusData.user.company_type) {
          statusData.user.company_type = statusData.user.platform.toLowerCase();
        }
        setUser(statusData.user);
        TokenManager.setUser(statusData.user);
        Cache.setUserScope(statusData.user.id);
      } else {
        // Token is invalid
        logout();
      }
    } catch (error) {
      // Token is invalid or network error
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus,
    // Impersonation
    isImpersonating,
    originalUser,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
